import type {
  SystemStyleObject} from '@chakra-ui/react'
import {
  Badge,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  Ellipsis,
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'brokertslib/src/components'
import { userListContext } from 'BrokerTsLib/src/context'
import type { UserGridItem } from 'BrokerTsLib/src/dtos'
import { getUserTypeName } from 'brokertslib/src/enums'
import type {
  ColumnDefinition,
  ColumnsDefinition,
} from 'brokertslib/src/interfaces/columnsDefinition'
import { useContext } from 'react'

import { TableWrapper } from '../../components/TableWrapper'
import { OrganizationLink } from '../shared/OrganizationLink'
import { UserLink } from '../shared/UserLink'
import { ValidityBadge } from '../shared/ValidityBadge'

export const UserListNameColumn: ColumnDefinition<UserGridItem> = {
  key: 'name',
  name: 'Jméno',
  cellExpr: (item) => (
    <UserLink {...item} sx={{ color: 'primary.500', fontWeight: 'medium' }} />
  ),
}

export const UserListOrganizationColumn: ColumnDefinition<UserGridItem> = {
  key: 'organization',
  name: 'Organizace',
  cellExpr: (item) => (
    <OrganizationLink
      name={item.organizationUnitName}
      id={item.organizationUnitId}
      sx={{ color: 'primary.500' }}
    />
  ),
}

export const UserListUserNameColumn: ColumnDefinition<UserGridItem> = {
  key: 'userName',
  name: 'Uživatelské jméno',
  cellExpr: (item) => (
    <Ellipsis sx={{ color: 'gray.500', maxW: '250px' }} disableTooltip>
      {item.userName}
    </Ellipsis>
  ),
}

export const UserListEmailColumn: ColumnDefinition<UserGridItem> = {
  key: 'email',
  name: 'E-mail',
  cellExpr: (item) => (
    <Text sx={{ color: 'primary.700', maxW: '250px' }}>{item.email}</Text>
  ),
}

export const UserListValidityColumn: ColumnDefinition<UserGridItem> = {
  key: 'valid',
  name: '',
  cellExpr: (item) => <ValidityBadge valid={item.valid} />,
  cellProps: { p: 1 },
}

export const UserListTypeColumn: ColumnDefinition<UserGridItem> = {
  key: 'type',
  name: '',
  cellExpr: (item) =>
    item.userTypeId ? (
      <Badge colorScheme="blue">{getUserTypeName(item.userTypeId)}</Badge>
    ) : (
      ''
    ),
  cellProps: { p: 1 },
}

export const UserListRolesColumn: ColumnDefinition<UserGridItem> = {
  key: 'roles',
  name: '',
  cellExpr: (item) =>
    item.roles && (
      <Flex sx={{ gap: 1 }}>
        {item.roles.map((role) => (
          <Badge key={role.role} colorScheme={'orange'}>
            {role.roleName}
          </Badge>
        ))}
      </Flex>
    ),
  cellProps: { p: 1 },
}

export const UserListDefaultColumns: ColumnsDefinition<UserGridItem> = [
  UserListNameColumn,
  UserListOrganizationColumn,
  UserListUserNameColumn,
  UserListEmailColumn,
  UserListValidityColumn,
  UserListTypeColumn,
  UserListRolesColumn,
]

export const UserList = ({
  users,
  sx,
  columnsDefinition = UserListDefaultColumns,
}: {
  users: Array<UserGridItem>
  withPaginator?: boolean
  sx?: SystemStyleObject
  columnsDefinition?: ColumnsDefinition<UserGridItem>
}) => {
  const { loading, pageSize } = useContext(userListContext)

  return (
    <TableWrapper sx={sx}>
      <Table variant="striped">
        <Thead>
          <Tr>
            {columnsDefinition &&
              columnsDefinition.map((column) => (
                <Th
                  key={column.key}
                  _empty={{ w: 0, p: 0 }}
                  {...column.headerCellProps}
                >
                  {column.name}
                </Th>
              ))}
          </Tr>
        </Thead>
        <Tbody>
          <TableSkeleton
            rowsCount={pageSize}
            colSpan={columnsDefinition.length}
            isLoaded={!loading}
          >
            <EmptyTablePlaceholder
              text={'Žádní uživatelé k zobrazení'}
              colSpan={columnsDefinition.length}
            >
              {users.map((user) => {
                return (
                  <Tr key={user.userId}>
                    {columnsDefinition &&
                      columnsDefinition.map((column) => (
                        <Td
                          key={column.key}
                          _empty={{ p: 0 }}
                          {...column.cellProps}
                        >
                          {column.cellExpr(user) || ''}
                        </Td>
                      ))}
                  </Tr>
                )
              })}
            </EmptyTablePlaceholder>
          </TableSkeleton>
        </Tbody>
      </Table>
    </TableWrapper>
  )
}
